/**
 * financialsMath — what the money clauses make true, derived.
 *
 * THE PANEL EXISTS BECAUSE THE NUMBERS ON THIS CANVAS ARE NOT INDEPENDENT. A weekly instalment
 * typed against a selling price implies a term, a final payment and a cost per missed week, and
 * an admin who cannot see those three has no way to notice that 550 a week against 180,000 is
 * six and a half years of Mondays. The prototype's own panel, in a module a test can hold.
 *
 * TWO RULES CARRIED OVER FROM THE FINANCIAL MEMORY, AND THEY ARE NOT STYLE:
 *
 * - THE FINAL INSTALMENT IS COMPUTED BY SUBTRACTION, never by a percentage or a remainder:
 *   `balance − (term − 1) × amount`. Every other form drifts by a few pesewas and publishes it.
 * - ROUNDING HAPPENS AT DISPLAY ONLY. Nothing here rounds an input before dividing with it.
 * - A MISSED WEEK IS SEVEN DAYS AT THE ENGINE'S DAILY RATE, `amount × rate/100 × 7/365`. The
 *   overdue calculator has no weekly rate to divide out — it derives a daily one over 365 days
 *   and accrues per day owed — so a figure taken as a fifty-second of the annual rate is a
 *   second, different answer to a question the contract already answers.
 *
 * `sold_at` IS THE VEHICLE PRICE ALONE. Insurance and loans are additive financing and never
 * subtract from it, so the balance is the price less the deposits and nothing else.
 *
 * DISPLAY-ONLY, AND NAMED AS SUCH against `.agents/rules/financial-parity.md`: none of the five
 * protected numbers (weekly_rate, overdue, total_value, total_paid, outstanding) is recomputed
 * here and nothing is written anywhere. This is arithmetic a driver could do on the same page.
 */

import { formatCurrency } from '@/lib/format';
import { instrumentFor } from '@/lib/agreements/instrument';

export interface MoneyClauseInput {
    /** `contracts.sold_at` — the vehicle price alone. */
    sellingPrice: number | null;
    /** SUM of the linked deposits. Null and 0 mean the same thing to the Deposit credited row. */
    depositTotal: number | null;
    /** The weekly instalment as typed, already parsed. */
    instalmentAmount: number | null;
    /** `contracts.overdue_interest_rate` — NOT NULL DEFAULT 365, so effectively never absent. */
    overdueRate: number | null;
}

export interface DerivedCell {
    label: string;
    /** Already formatted. `—` when the inputs cannot answer it. */
    value: string;
    /** A quieter second half of the value, e.g. `3 years 20 weeks`. */
    sub?: string;
    /** Nothing to say yet — drawn muted. */
    empty?: boolean;
}

export interface DerivedConsequences {
    /** Price less deposits. Null when there is no price. */
    balance: number | null;
    /** Weeks of instalments the amount implies, by division. */
    term: number | null;
    /** The last payment, by SUBTRACTION. */
    finalInstalment: number | null;
    /** One week of arrears on one instalment, in cedis. */
    overduePerWeek: number | null;
    cells: DerivedCell[];
    note: string;
    /** The term is longer than anything the business writes — the note goes destructive. */
    alarming: boolean;
}

const EMPTY = '—';

/** Longer than five years of Mondays: not a contract Primelane writes, so the note says so. */
export const LONG_TERM_WEEKS = 260;

/**
 * The calculator's own year and week, restated rather than imported.
 *
 * The engine is `public.calculate_overdue_interest` in the database — one implementation for every
 * client, which is why there is nothing here to import. These two constants are read off it
 * (`DAYS_PER_YEAR = 365`, Monday-to-Monday weeks) and are the whole of what this panel needs to
 * price a missed week the same way.
 */
const DAYS_PER_YEAR = 365;
const DAYS_PER_MISSED_WEEK = 7;

/**
 * The overdue rate AS THE INSTRUMENTS PRINT IT — per day, to four places, trailing zeros trimmed.
 *
 * THE PRINTED FIGURE IS THE OPERATIVE ONE. The hire purchase, loan and insurance-loan schedules
 * all state arrears interest as a DAILY percentage, so `agreement_merge_data` emits
 * `contract.overdue_daily_rate` as `rtrim(rtrim(to_char(rate / 365, 'FM990.0000'), '0'), '.')` —
 * `rate / 365` being the same derivation `DAYS_PER_YEAR` makes below and both overdue-calculator
 * ports make. FOUR PLACES AND NOT TWO: 400 per cent a year is 1.0959 a day, and two places would
 * put 1.1 on a signed page against a charged 1.0959. A new contract is born at 365 — 1 a day
 * exactly, which needs no places at all — but the live contracts that need the precision are
 * exactly the ones this formatter exists for.
 *
 * `toFixed` then the two trims, in that order, is the JS spelling of that SQL — 0 stays "0", and
 * 100 per cent a year prints "0.274" rather than "0.2740".
 */
export function dailyRateText(annual: number | null | undefined): string | null {
    if (annual === null || annual === undefined || Number.isNaN(annual)) return null;
    return (annual / 365).toFixed(4).replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * Dust: less than half a pesewa left over buys no extra instalment.
 *
 * The same guard `agreement_merge_data` applies when it derives `contract.term_weeks`
 * (`20260813010000`), which is in turn the loan engine's own `buildLoanPeriod` rule.
 */
const TERM_DUST = 0.005;

/**
 * The term the hire purchase SCHEDULE now prints, derived the way the server derives it.
 *
 * FLOOR AND A DUST GUARD, NEVER `Math.ceil` — and the difference is a whole week on the signed
 * page. `agreement_merge_data` takes the whole weeks by floor, the last part-week by SUBTRACTION,
 * and adds an instalment only where that remainder is worth half a pesewa or more; `ceil` turns a
 * tenth of a pesewa of floating-point residue into a 111th instalment. Until the Schedule carried
 * `{{contract.term_weeks}}` this panel's figure answered to nobody, so the two spellings could
 * differ unnoticed. It is printed on an instrument now, and this is the one derivation.
 *
 * Null wherever the server states no term at all: no price, no weekly figure, or a deposit that
 * covers the price outright. Stating 0 weeks would be a fact rather than the absence of one.
 */
export function termWeeks(balance: number | null, instalment: number | null | undefined): number | null {
    if (balance === null || balance <= 0) return null;
    if (!instalment || instalment <= 0) return null;
    const fullWeeks = Math.floor(balance / instalment);
    const final = balance - fullWeeks * instalment;
    return fullWeeks + (final >= TERM_DUST ? 1 : 0);
}

// ─── The advance's repayment block ───────────────────────────────────────────

/**
 * One `insurance_loan_instalments` row, as a derivation needs it.
 *
 * Both columns are NOT NULL in SQL (`amount` numeric, `starts_at` date), so a real read always
 * carries both. Nullable here for the same reason `ContractShape` is: a select that forgets to ask
 * hands back `undefined` rather than a figure, and the derivation below has to say so rather than
 * treat the gap as a zero.
 */
export interface AdvanceInstalmentRow {
    amount: number | null;
    /** A DATE column, so PostgREST hands over `'YYYY-MM-DD'` — never an instant. */
    startsAt: string | null;
}

/** One `insurance_loans` row and its rate history, in the vocabulary the resolver uses. */
export interface AdvanceFacts {
    /**
     * `insurance_loans.amount` — the PREMIUM for the year, and not the amount advanced [D-B46].
     *
     * The two were the same number until the driver's own part-payment came off the advance. What
     * Primelane put in is this figure less `depositTotal`; the Schedule prints both, one row apart.
     */
    principal: number | null;
    /** `insurance_loans.interest_rate`, per cent, charged once. */
    rate: number | null;
    instalments: AdvanceInstalmentRow[];
    /**
     * Σ of the transactions behind this loan's `insurance_loan_deposits` [D-B45].
     *
     * REQUIRED, so that a caller which has not been widened is a typecheck failure rather than a
     * schedule silently derived for a premium nobody borrowed. Pass `null` where the deposits have
     * not been read: `instrumentFor` answers `ADVANCE` for an unknown, which is this function's
     * behaviour before the field existed.
     */
    depositTotal: number | null;
}

export interface AdvanceRepayments {
    /**
     * The Amount advanced — the premium LESS what the Borrower paid towards it [D-B46].
     *
     * HELD HERE SO NOBODY SPELLS IT TWICE. It is the base of the interest, the base of the total
     * and a printed row in its own right, and a second `amount − deposits` beside any of them is
     * how the dialog and the document come to disagree. `0` on a premium paid in full, which is
     * the Record's own "Amount advanced by Primelane: nil".
     */
    advanced: number | null;
    /** The Total repayable, held ONCE so the interest can be taken off it. */
    total: number | null;
    /** The Interest row, by SUBTRACTION off that total. */
    interest: number | null;
    /** The Weekly repayment: the LATEST rate row. */
    weekly: number | null;
    /** The First repayment: the EARLIEST start, still a `'YYYY-MM-DD'` DATE string. */
    firstStartsAt: string | null;
    /** Over how many weeks, amortising the TOTAL and not the principal. */
    term: number | null;
    /**
     * DID THE DRIVER PAY THIS PREMIUM IN FULL [D-B45] — the two limbs of `instrumentFor`, answered
     * once here so no caller re-derives them beside the figures they belong to.
     *
     * A `true` CHANGES WHAT THE THREE SCHEDULE FIGURES MEAN, which is why it travels with them
     * rather than beside them. `weekly`, `firstStartsAt` and `term` are null on a paid-in-full year
     * because there is no schedule and there never will be one — not because a schedule has not
     * been recorded yet — and a surface that read them as unfilled would ask an admin to go and
     * enter a repayment plan for money that is already paid.
     */
    paidInFull: boolean;
}

const NO_ADVANCE: AdvanceRepayments = {
    advanced: null,
    total: null,
    interest: null,
    weekly: null,
    firstStartsAt: null,
    term: null,
    paidInFull: false,
};

/**
 * The figures the Insurance Premium Advance's Schedule prints, derived the way the server
 * derives them (`agreement_merge_data`, `20260816010000`, net at [D-B46]).
 *
 * THE AMOUNT ADVANCED IS THE PREMIUM LESS WHAT THE BORROWER PAID, AND THE INTEREST IS CHARGED ON
 * THAT. The owner's ruling: *"when a driver pays part of the premium, the interest is only
 * calculated on the remaining amount advanced by Primelane. I thought this has always been the
 * case."* It always was in the ledger — `get_contract_insights` and `calculate_overdue_interest`
 * have charged interest on `amount − deposits` throughout — and it never was in the two places
 * that spelled the PRINCIPAL gross: the allocator's cap and the resolver this port mirrors. So
 * `A − D` is the base of the interest, the base of the total and a printed row of its own, and
 * the clamp at zero is `GREATEST(A − D, 0)` from the resolver: the second limb of `instrumentFor`
 * keeps a financed advance on this branch even after its deposits cover the premium, and a
 * negative amount advanced is not a figure the Schedule can print.
 *
 * ONE TOTAL, HELD ONCE, READ TWICE — AND THE INTEREST IS A SUBTRACTION, NEVER A SECOND
 * PERCENTAGE. The Schedule prints the Amount advanced, the Interest and the Total repayable on
 * three consecutive rows and says in the third that it is the first two added. `net × rate / 100`
 * is the same number in exact arithmetic and is NOT guaranteed to be the same number in the
 * double precision `insurance_loans.amount` and `interest_rate` are stored in, so the only
 * spelling that keeps faith with that sentence is `total − net` off the total this function
 * already holds. (The three rows are still rounded independently AT DISPLAY, so the printed rows
 * add up on the same precondition the server states: an amount advanced carried to at most two
 * decimals.)
 *
 * THE INSTALMENTS ARE A RATE HISTORY, NOT AN AMORTISATION TABLE. The weekly figure is the LATEST
 * row by `starts_at` (`ORDER BY starts_at DESC LIMIT 1`) and the first repayment is the EARLIEST
 * start (`MIN(starts_at)`) — the only date this schema records for when an advance begins to be
 * repaid. `starts_at` is a DATE, so PostgREST hands over `'YYYY-MM-DD'` and a STRING comparison is
 * the chronological one: no parse, no zone, and no chance of the host clock moving a boundary.
 * Two rows sharing a start date are indeterminate on the server; this port takes the one recorded
 * last, which is the newest rate on the day it changed.
 *
 * THE TERM AMORTISES THE TOTAL REPAYABLE AND NOT THE PRINCIPAL, through the same `termWeeks` the
 * hire purchase Schedule's own Term goes through — floor, a part-week by subtraction, the same
 * half-pesewa dust guard. The interest is charged once at the outset and is part of what the
 * weekly repayment repays, so a term over the principal alone would state a schedule that stops
 * short of the figure printed two rows above it.
 *
 * EVERY GUARD IS A NULL AND NOT A ZERO, for the reason the contract's term states: an advance with
 * no weekly row has no term to state, and 0 weeks is a fact rather than the absence of one.
 *
 * A PREMIUM THE DRIVER PAID IN FULL TAKES THE OTHER BRANCH AND HAS NO SCHEDULE AT ALL [D-B45].
 * Where the deposits cover the amount and no instalment row exists, `agreement_create` publishes an
 * Insurance Premium Payment Record instead of an Advance: nothing is advanced, no interest arises
 * and nothing is owed. So the total repayable IS the premium, the interest is a KNOWN ZERO rather
 * than an absence — the server's `total_value` charges interest on `(amount − deposits)`, which is
 * zero here whatever the stored rate says — and the three schedule figures stay null with
 * `paidInFull` beside them to say why. THAT BRANCH IS UNMOVED BY [D-B46]: the Record states a
 * premium and what was paid against it, never a repayable, so netting a deposit out of the one
 * money figure it prints would zero the page.
 */
export function advanceRepayments(loan: AdvanceFacts | null | undefined): AdvanceRepayments {
    if (!loan) return NO_ADVANCE;

    const principal = loan.principal;

    if (
        instrumentFor({
            amount: principal,
            depositTotal: loan.depositTotal,
            instalmentCount: loan.instalments.length,
        }) === 'PAYMENT_RECORD'
    ) {
        return {
            // NIL, WHICH IS THE RECORD'S OWN WORD FOR IT. The premium below is what the year cost;
            // this is what Primelane put in, and on this branch that is nothing.
            advanced: 0,
            total: principal,
            interest: 0,
            weekly: null,
            firstStartsAt: null,
            term: null,
            paidInFull: true,
        };
    }
    // THE AMOUNT ADVANCED, NET OF THE BORROWER'S OWN PAYMENT [D-B46] — `GREATEST(A − D, 0)`, the
    // resolver's spelling. A NULL `depositTotal` is deposits nobody read rather than deposits that
    // are nought, and it falls to the gross figure for the reason `instrumentFor` falls to the
    // advance: an unread total must not quietly shrink what the page says was lent.
    const net =
        principal === null || principal === undefined
            ? null
            : Math.max(principal - (loan.depositTotal ?? 0), 0);
    // THE GROUPING IS THE RESOLVER'S, CHARACTER FOR CHARACTER: `20260816010000` computes
    // `v_iloan_net + v_iloan_net * COALESCE(v_iloan_rate, 0) / 100` over two `double precision`
    // operands, and `*` and `/` are left-associative at equal precedence — so the multiply lands
    // first and the divide second. JS doubles ARE those doubles, so this spelling is bit-exact with
    // the FLOAT8 EXPRESSION and equal at display after the cast, while `n * (r / 100)` is a
    // different rounding: an advance of 1,078 at 12 per cent is 1207.3599999999999 that way and
    // 1207.3600000000001 this way (705 such pairs in the ordinary 1,000–12,000 range alone). The
    // resolver does not stop at the float — it casts `::numeric`, which keeps 15 significant
    // digits, so `(1078 + 1078 * 12 / 100)::numeric` is 1207.36 flat where JS still holds the
    // 1-ulp tail. Both print GH₵1,207.36, and the term below (`floor(total / weekly)` against a
    // half-pesewa dust guard) absorbs a last-place bit in either direction. Matching the grouping
    // is therefore about staying on the server's side of that guard, not about a difference the
    // page could show.
    const total = net === null ? null : net + (net * (loan.rate ?? 0)) / 100;
    const interest = total === null ? null : total - (net as number);

    // A row with no start has no place in either ordering — `ORDER BY` and `MIN` are both being
    // asked a question about dates. The column is NOT NULL, so this only ever drops a row a
    // select failed to ask for.
    const dated = loan.instalments.filter((row) => !!row.startsAt);
    let latest: AdvanceInstalmentRow | null = null;
    let firstStartsAt: string | null = null;
    for (const row of dated) {
        const startsAt = row.startsAt as string;
        // `>=` and not `>`: the last row recorded on a shared start date wins, which is the tie the
        // server leaves open and the one an admin means by "the current rate".
        if (latest === null || startsAt >= (latest.startsAt as string)) latest = row;
        if (firstStartsAt === null || startsAt < firstStartsAt) firstStartsAt = startsAt;
    }

    const weekly = latest?.amount ?? null;
    return {
        advanced: net,
        total,
        interest,
        weekly,
        firstStartsAt,
        term: termWeeks(total, weekly),
        paidInFull: false,
    };
}

function weeksText(term: number): string {
    const years = Math.floor(term / 52);
    const weeks = term % 52;
    if (!years) return '';
    return (
        `${years} ${years === 1 ? 'year' : 'years'}` +
        (weeks ? ` ${weeks} ${weeks === 1 ? 'week' : 'weeks'}` : '')
    );
}

export function derivedConsequences(input: MoneyClauseInput): DerivedConsequences {
    const { sellingPrice, depositTotal, instalmentAmount, overdueRate } = input;
    const balance =
        sellingPrice === null || sellingPrice === undefined
            ? null
            : sellingPrice - (depositTotal ?? 0);

    const term = termWeeks(balance, instalmentAmount);
    const finalInstalment =
        term === null ? null : (balance as number) - (term - 1) * (instalmentAmount as number);
    // ONE MISSED WEEK, PRICED THE WAY THE ENGINE PRICES IT: the overdue calculator turns the
    // annual rate into a DAILY one over 365 days (`public.calculate_overdue_interest`:
    // `v_daily_rate := (v_rate / 100) / 365`) and accrues it per day owed — so a week of arrears
    // on one instalment is seven of those days, not a fifty-second of a year. Dividing by 52
    // overstated every figure in this cell by 52/(365/7) ≈ 1.003 — small, and still a number an
    // admin could check against the contract's own interest tab and find different.
    const overduePerWeek =
        !instalmentAmount || overdueRate === null || overdueRate === undefined
            ? null
            : (instalmentAmount * (overdueRate / 100) * DAYS_PER_MISSED_WEEK) / DAYS_PER_YEAR;

    const cells: DerivedCell[] = [
        term === null
            ? { label: 'Term', value: EMPTY, empty: true }
            : { label: 'Term', value: `${term} weeks`, sub: weeksText(term) || undefined },
        finalInstalment === null
            ? { label: 'Final instalment', value: EMPTY, empty: true }
            : { label: 'Final instalment', value: formatCurrency(finalInstalment) },
        overduePerWeek === null
            ? { label: 'Overdue interest, per missed week', value: EMPTY, empty: true }
            : { label: 'Overdue interest, per missed week', value: formatCurrency(overduePerWeek) },
        sellingPrice === null || sellingPrice === undefined
            ? { label: 'Total the Hirer repays', value: EMPTY, empty: true }
            : { label: 'Total the Hirer repays', value: formatCurrency(sellingPrice) },
    ];

    let note: string;
    let alarming = false;
    if (term === null) {
        const rate = overdueRate === null || overdueRate === undefined ? null : overdueRate;
        note = balance === null
            ? 'Enter a selling price to see the balance the instalments have to discharge.'
            : `Enter a weekly instalment to see the term it implies${rate === null ? '' : `, and what a missed week costs at ${rate}% per annum`}.`;
    } else if (term > LONG_TERM_WEEKS) {
        alarming = true;
        note =
            `That is ${term} weeks of payments — longer than any hire purchase Primelane writes. ` +
            'Check the amount before you save.';
    } else {
        note =
            `A deposit of ${formatCurrency(depositTotal ?? 0)} plus ${term} instalments settles the ` +
            `selling price of ${formatCurrency(sellingPrice as number)} exactly; the last one is ` +
            `reduced to ${formatCurrency(finalInstalment as number)}.`;
    }

    return { balance, term, finalInstalment, overduePerWeek, cells, note, alarming };
}

/**
 * The number a money input holds, or null.
 *
 * An input is a string until somebody proves otherwise, and `Number('')` is 0 — which would put a
 * selling price of nothing into the panel as a real figure and derive a term from it.
 */
export function parseAmount(value: string | null | undefined): number | null {
    if (value === null || value === undefined) return null;
    const cleaned = String(value).replace(/[^0-9.-]/g, '');
    if (cleaned === '' || cleaned === '-' || cleaned === '.') return null;
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
}
